import PropTypes from 'prop-types';
import React, { useEffect, useState, useMemo } from 'react';
import classNames from 'classnames/bind';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import countBy from 'lodash/countBy';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import _get from 'lodash/get';
import some from 'lodash/some';
import find from 'lodash/find';
import has from 'lodash/has';
import findIndex from 'lodash/findIndex';
import List from 'yoda-core-components/lib/components/List/List';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import getBrowserHistory from 'yoda-core-components/lib/navigation/history/getBrowserHistory';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import ImgNotAvailablePdp from 'yoda-core-components/lib/assets/svg/ImgNotAvailablePdp.svg';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import Cookies from 'yoda-core-components/lib/helpers/Cookies/Cookies';
import { parseJson } from 'yoda-core-components/lib/helpers/Utils/Utils';
import Messages, { messagesLevels, messagesActions as MessagesActions } from '../Messages';
import * as styles from './ProductOptions.css';
import {
    productOptionsOnChangeAction,
    resetATCClick,
    productOptionsOnResetAction,
} from '../../actions/ProductOptionsAction';
import { productOptionsOnClickAction } from '../../actions/ProductOptionsForCongruentPPAction';
import { colorSwatchesHoverAction } from '../../actions/ColorSwatchesAction';
import { getInventoryAction } from '../../actions/InventoryAction';
import { productOOSAnalytics, allSKUSelected } from '../../actions/AnalyticsAction';
import ProductModule, {
    initProductOptions,
    selectReInitializeDimensions,
    selectedProductSkus,
    productImageOnhover,
    selectCurrentFilters,
} from '../../selectors/ProductOptionsSelector';
import selectProductType from '../../selectors/productDetails/selectProductType';
import productPricingSelector, {
    bopisIncentiveProductEligibilitySelector,
    selectSaleandClearence,
} from '../../selectors/ProductPricingSelector';
import { selectSkuId } from '../../selectors/productDetails/selectClearanceAndCloseout';
import ProductConstants, { bopisIncentive } from '../../common/ProductConstants';
import SelectDropdown from '../SelectDropdown/SelectDropdown';
import KnowledgeAssistance from '../KnowledgeAssistance/KnowledgeAssistance';
import TrueToSizeIndicator from '../TrueToSizeIndicator/TrueToSizeIndicator';
import CustomOptionsConnected from '../CustomOptions/CustomOptions';
import { CUSTOM_OPTIONS_FORM } from '../CustomOptions/CustomOptions.config';
import { getSdpuStoreData } from '../../actions/SdpuStoreInfoAction';
import { resetImageZoomStatus } from '../../actions/PdpImageZoomAction';
import { selectDeviceName, selectDeviceType } from '../../selectors/ContextSelector';
import DataSizeChart from '../DataSizeChart/DataSizeChart';
import {
    LOCATION_SERVICE_COOKIES_AKAMAI_STORE,
    LOCATION_SERVICE_COOKIES_USER_PREFERRED_STORE,
} from '../../common/Constants';
import ProductOptionPills from '../ProductOptionPills/ProductOptionPills';
import ProductOptionsPlaceHolder from './ProductOptionsPlaceHolder';
import BeautyDropDown from './BeautyDropDown';
import {
    selectInventoryWrapper,
    selectMessagesWrapper,
    selectProductOfferingBySource,
} from '../../selectors/PdpNameSpaceSelector';
import handleMobileColorSelectionBehaviour, {
    shouldRenderMobileView,
} from '../../helpers/Utils/mobileColorSelectionHelper';
import {
    getMaxSwatchLengthForMobile,
    shouldDimensionPriceDisplay,
    storeSkuInfo,
    renderPriceForDimensions,
    getImageObject,
} from '../../helpers/ProductOptions/ProductOptionsHelper';
import { getDeliveryDateAnalytics } from '../../helpers/GetDeliveryDateAnalytics/getDeliveryDateAnalytics';
import SecondaryDimension from './SecondaryDimension';

const cx = classNames.bind(styles);
const productType = ProductConstants.REGULAR;
let renderSecondaryDimensionValue = '';

function isSizeDimension(dimension) {
    return dimension.id === 'SIZE' || dimension.id === 'WAIST' || dimension.id === 'CHEST';
}

export function replaceColorInDimensions(dimensions = []) {
    const colorDimensions = [];
    const dimensionsWithoutColor = [];

    forEach(dimensions, (item) => {
        if (item.id === 'COLOR') {
            colorDimensions.push(item);
        } else {
            dimensionsWithoutColor.push(item);
            // eslint-disable-next-line no-param-reassign
            item.isNeedToBePadded = true;
        }
    });

    return [...colorDimensions, ...dimensionsWithoutColor, { bvTrueToFit: true }];
}

/* istanbul ignore next */
export const ProductOptions = (props) => {
    const dispatch = useDispatch();

    // States
    const [isOOSAnalyticComplete, setIsOOSAnalyticComplete] = useState(false);
    const [selectedColorValue, setSelectedColorValue] = useState('');
    const [showMoreLessClicked, setShowMoreLessClicked] = useState(false);
    const [viewMoreSwatches, setViewMoreSwatches] = useState(false);
    const [storeID, setStoreID] = useState('');

    // Select
    const inventory =
        useSelector((state) => selectInventoryWrapper(props.fromMiniPDP)(state)) || [];
    const deviceType = useSelector((state) => selectDeviceType(state)) || {};
    const deviceName = useSelector((state) => selectDeviceName(state)) || 'desktop';
    const productOfferingData =
        useSelector((state) => selectProductOfferingBySource(props.fromMiniPDP)(state)) || {};
    const productOptions =
        useSelector((state) =>
            initProductOptions(props.fromMiniPDP ? props.fromMiniPDP : undefined)(state)
        ) || {};
    const messages = useSelector((state) => selectMessagesWrapper(props.fromMiniPDP)(state)) || {};
    const selectedStore = useSelector(
        (state) =>
            state.selectedStore && state.selectedStore.storeDetails
                ? state.selectedStore.storeDetails
                : {},
        shallowEqual
    );
    const reInitializeDimensions =
        useSelector((state) => selectReInitializeDimensions(props.fromMiniPDP)(state)) || [];
    const selectedProductSKU =
        useSelector((state) => selectedProductSkus(props.fromMiniPDP)(state)) || [];
    const featureFlags = useSelector((state) => state.context.featureFlags) || {};
    const colorSwatches =
        useSelector((state) => productImageOnhover(props.fromMiniPDP)(state)) || {};
    const skuId = useSelector((state) => selectSkuId(props.fromMiniPDP)(state)) || '';
    const sdpuStoreInfo = useSelector((state) => state.sdpuStoreInfo) || {};
    const congruentSelected = useSelector((state) => state.congruentSelected) || {};
    const currentFilters = useSelector((state) => selectCurrentFilters(props.fromMiniPDP)(state));
    const isSelectedItemAvailable =
        useSelector(
            (state) =>
                state.deliveryOptionsName && state.deliveryOptionsName.isSelectedItemAvailable
        ) || false;
    const pdpProductType =
        useSelector((state) => selectProductType(props.fromMiniPDP)(state)) || {};
    const productPillsLimit = useSelector((state) =>
        _get(state, 'context.preferences.apollo.productPillsLimit', 9)
    );
    const bopisIncentiveProductEligibility =
        useSelector((state) =>
            bopisIncentiveProductEligibilitySelector(props.fromMiniPDP)(state)
        ) || {};
    const saleAndClearence = useSelector((state) =>
        selectSaleandClearence(props.fromMiniPDP)(state)
    );
    const pricingDetails = useSelector((state) => productPricingSelector(props.fromMiniPDP)(state));
    const userPreferredStore = parseJson(
        Cookies.load(LOCATION_SERVICE_COOKIES_USER_PREFERRED_STORE)
    );
    let hasExecuted = false;

    let selectedSwatchSkuValue = '';

    let displayTopSizeChart = false;
    const sizeGuide = _get(props.productDetails, 'sizeGuide', {});
    const isSizeGuideAvailable =
        !isEmpty(Object.keys(sizeGuide)) && !featureFlags.disableDataSizeChart;
    if (
        (!isSizeGuideAvailable && !featureFlags.disableDataSizeChart) ||
        featureFlags.disableDataSizeChart
    ) {
        displayTopSizeChart = true;
    } else if (isSizeGuideAvailable && !featureFlags.disableDataSizeChart) {
        displayTopSizeChart = true;
    }

    const onColorSwatchEnter = (dimension, { value }) => {
        const { fromMiniPDP } = props;
        const updateProductImage = fromMiniPDP || !fromMiniPDP;

        renderSecondaryDimensionValue = (
            <SecondaryDimension dimension={dimension} option={value} fromMiniPDP={fromMiniPDP} />
        );

        dispatch(colorSwatchesHoverAction(value, true, updateProductImage, fromMiniPDP));
    };

    const debouncedOnColorSwatchEnter = debounce(onColorSwatchEnter, 100);

    const onColorSwatchLeave = (primarySku) => {
        const { fromMiniPDP } = props;
        const value = selectedSwatchSkuValue || primarySku;

        dispatch(colorSwatchesHoverAction(value, false, true, fromMiniPDP));
    };

    const debouncedOnColorSwatchLeave = debounce(onColorSwatchLeave, 100);

    // Methods
    const triggerSDPUStoreInfo = () => {
        const { query } = props;
        const SDPUStoreInfo = LocalStorage.getData('SDPUStoreInfo', true);
        const galleryStoreInfo = LocalStorage.getData('gallerySDPUStoreInfo', true);
        const akamaiStore = Cookies.load(LOCATION_SERVICE_COOKIES_AKAMAI_STORE);

        if (query.storeIds && galleryStoreInfo) {
            const galleryStoreData = parseJson(galleryStoreInfo);
            dispatch(getSdpuStoreData(galleryStoreData));
        } else if (SDPUStoreInfo) {
            const SDPUStoreData = parseJson(SDPUStoreInfo);
            dispatch(getSdpuStoreData(SDPUStoreData));
        } else if (userPreferredStore) {
            dispatch(getSdpuStoreData(userPreferredStore));
        } else if (akamaiStore) {
            const akamaiStoreData = parseJson(akamaiStore);
            dispatch(getSdpuStoreData(akamaiStoreData));
        }
    };

    const getInventory = () => {
        const { productDetails, query, fromMiniPDP } = props;

        /* istanbul ignore next */
        if (productDetails.id) {
            let selectedStoreID = '';
            const defaultStore = LocalStorage.getData('defaultStore', true);
            /* istanbul ignore next */
            const storeFromLocal = defaultStore ? JSON.parse(defaultStore) : {};
            const galleryStoreInfo = JSON.parse(LocalStorage.getData('gallerySDPUStoreInfo', true));
            const akamaiStore = Cookies.load(LOCATION_SERVICE_COOKIES_AKAMAI_STORE);

            if (selectedStore.id) {
                selectedStoreID = selectedStore.id;
                /* istanbul ignore next */
            } else if (storeFromLocal.id) {
                selectedStoreID = storeFromLocal.id;
            }

            if (query.storeIds && galleryStoreInfo && galleryStoreInfo.storeId) {
                selectedStoreID = galleryStoreInfo.storeId;
            }
            if (userPreferredStore) {
                selectedStoreID = userPreferredStore.storeId;
            }

            // use Akamai store if no storeID is available
            if (!selectedStoreID) {
                if (akamaiStore) {
                    const akamaiStoreData = parseJson(akamaiStore);
                    selectedStoreID = akamaiStoreData.storeId;
                }
            }

            setStoreID(selectedStoreID);

            const inventoryPayload = {
                PPID: productDetails.id,
                storeID: selectedStoreID,
                sourceMiniPDP: fromMiniPDP,
            };
            inventoryPayload.url = _get(productDetails, 'additionalDetails.url', '');

            dispatch(getInventoryAction(inventoryPayload));
        }
    };

    const onProductOptionsChange = (dimensionName, option, priceType) => {
        dispatch(
            productOptionsOnChangeAction(
                {
                    option,
                    dimensionName,
                    sourceMiniPDP: props.fromMiniPDP,
                },
                priceType
            )
        );
        dispatch(resetATCClick());
        dispatch(resetImageZoomStatus(false));
    };

    const onChangeHandler = (dimension, dataItem, value, e) => {
        const { fromMiniPDP, productDetails } = props;
        const { enableColorCarousel = true, enableErrorSizing } = featureFlags;
        const { optionsSelected = [] } = productOptions;
        const congruentOptions =
            !fromMiniPDP && featureFlags.enableProductSelection
                ? _get(productDetails, 'congruentOptions', [])
                : [];

        onProductOptionsChange(dimension.name, value, dimension.priceType);
        // remove all the messages
        if (!enableErrorSizing) {
            dispatch(
                MessagesActions.removeAllMessagesExcept({
                    section: CUSTOM_OPTIONS_FORM,
                    sourceMiniPDP: fromMiniPDP,
                })
            );
        } else {
            dispatch(
                MessagesActions.removeMessage({
                    id: messages[dimension.id]?.[0]?.id,
                    section: dimension.id,
                    sourceMiniPDP: fromMiniPDP,
                })
            );
        }

        // eslint-disable-next-line no-param-reassign
        dataItem =
            dataItem ||
            dimension.options.filter(
                ({ value: optionValue }) => `${optionValue}` === `${value}`
            )[0] ||
            {};

        /* istanbul ignore else */

        /* MPDPYODA-3545 - Select the color by default if there is only one available  */
        // incase if color is not selected by default and has only one color select it by default
        const dimensionsColor = find(_get(productDetails, 'dimensions', []), { id: 'COLOR' });
        const availableColorsRow = filter(
            _get(dimensionsColor, 'options', []),
            (option) => option.availabilityMessage !== ''
        );
        const selectedColor =
            optionsSelected && optionsSelected.filter((option) => option.dimensionName === 'color');
        if (availableColorsRow.length === 1 && !selectedColor) {
            const dimensionText = _get(availableColorsRow[0], 'value', '');
            dispatch(colorSwatchesHoverAction(dimensionText, false, true, fromMiniPDP));
            dispatch(
                productOptionsOnChangeAction({
                    option: dimensionText,
                    dimensionName: 'color',
                    sourceMiniPDP: props.fromMiniPDP,
                })
            );
        }
        if (
            dimension.id === 'COLOR' &&
            shouldRenderMobileView(window?.innerWidth) &&
            enableColorCarousel
        ) {
            const colorUnavailable =
                typeof dataItem.availableInStock !== 'undefined'
                    ? !dataItem.availableInStock
                    : null;
            const brokenSwatch = fromMiniPDP ? colorUnavailable : false;
            let colorList = null;
            colorList = e?.target?.closest && e?.target?.closest('#productOptionsColorList');
            handleMobileColorSelectionBehaviour(
                value,
                colorList,
                null,
                24,
                brokenSwatch
            ); /* scrollItem has left padding of 24px, hence , added this to make the selected swatch look centered */
        } else if (congruentOptions.length > 0 && deviceType.isMobile)
            handleMobileOptionsSelectionBehaviour(dimension, value, 24);
    };

    const getDefaultValueSelected = (type, priceType) => {
        const { productDetails } = props;
        const { optionsSelected = [], ppFeatureColor = '', dimensions = [] } = productOptions;
        let optionValue;
        if (priceType && find(optionsSelected, (item) => has(item, 'priceType'))) {
            optionValue =
                optionsSelected &&
                optionsSelected.length &&
                optionsSelected.filter(
                    (optionSelected) =>
                        optionSelected.type === type && optionSelected.priceType === priceType
                );
        } else {
            optionValue =
                optionsSelected &&
                optionsSelected.length &&
                optionsSelected.filter((optionSelected) => optionSelected.type === type);
        }
        const selectedColor = optionsSelected.filter((option) => option.dimensionName === 'color');
        const colorValue = dimensions.filter((optionSelected) => optionSelected.id === 'COLOR');
        let exactPriceType;
        if (colorValue.length > 1) {
            colorValue.forEach((option) => {
                const value = option.options.filter(
                    (optionSelected) =>
                        optionSelected.value ===
                        (selectedColor[0] && selectedColor[0].name
                            ? selectedColor[0].name
                            : ppFeatureColor)
                );
                if (value.length > 0) {
                    exactPriceType = option.priceType;
                }
            });
        }
        if (
            _get(featureFlags, 'enablePPFeatureFlag', true) &&
            productDetails.hybridIndicator &&
            (selectedColor.length === 0 || selectedColor.length === 1) &&
            selectedColorValue !== exactPriceType &&
            exactPriceType
        ) {
            setSelectedColorValue(exactPriceType);
            dispatch(
                productOptionsOnChangeAction(
                    {
                        option:
                            selectedColor[0] && selectedColor[0].name
                                ? selectedColor[0].name
                                : ppFeatureColor,
                        dimensionName: 'color',
                        sourceMiniPDP: props.fromMiniPDP,
                    },
                    exactPriceType
                )
            );
        }
        return optionValue && optionValue[0] ? optionValue[0].option : '';
    };

    const getMaxSwatchLengthForDevice = useMemo(() => {
        const { fromMiniPDP, productDetails } = props;
        const { enableColorCarousel = true } = featureFlags;
        const mobileWindowPadding = fromMiniPDP ? 40 : 32;

        let swatchCount = 0;
        const desktopSwatchCount = fromMiniPDP ? 17 : 9;
        let colorCarouselCount = 0;
        let colorCarouselCountTablet = 0;

        if (fromMiniPDP) {
            colorCarouselCount = productDetails.hybridIndicator ? 11 : 17;
        } else {
            colorCarouselCount = productDetails.hybridIndicator ? 13 : 19;
        }

        if (fromMiniPDP) {
            colorCarouselCountTablet = productDetails.hybridIndicator ? 13 : 20;
        } else {
            colorCarouselCountTablet = productDetails.hybridIndicator ? 11 : 17;
        }
        switch (deviceName) {
            case 'tablet':
                swatchCount = enableColorCarousel ? colorCarouselCountTablet : 20;
                break;
            case 'mobile':
                // number of swatches displayed per line (excluding the more/less btn)
                swatchCount = getMaxSwatchLengthForMobile(mobileWindowPadding);
                break;
            default:
                swatchCount = enableColorCarousel ? colorCarouselCount : desktopSwatchCount;
        }

        return swatchCount;
    }, [deviceName]);

    const swatchesMaxLn = getMaxSwatchLengthForDevice;

    const shouldViewMoreSwatches = (dimensionIndex) => {
        const { optionsSelected = [] } = productOptions;
        const colorDimensions = reInitializeDimensions.filter(
            (dimension) => dimension.id === 'COLOR'
        )[0] || {
            options: [],
        };
        const selectedColor =
            optionsSelected.filter((option) => option.dimensionName === 'color')[0] || {};
        const color =
            colorDimensions.options.find((option) => option.value === selectedColor.name) || {};
        const isColorAvailable = color.availableInStock;

        if (
            dimensionIndex >= swatchesMaxLn &&
            (!showMoreLessClicked || (showMoreLessClicked && !isColorAvailable))
        ) {
            setShowMoreLessClicked(false);
            setViewMoreSwatches(true);
        }
    };

    const showMoreLessOnClick = (e) => {
        e.preventDefault();
        setViewMoreSwatches(!viewMoreSwatches);
        setShowMoreLessClicked(true);
    };

    const handleMobileOptionsSelectionBehaviour = (dimension, value, optionScrollLeft) => {
        if (!__SERVER__) {
            /* istanbul ignore next */
            const dimValue = value || dimension?.options[0]?.value;
            const scrollItem = document.querySelector(`#${dimension?.id || dimension?.type}`) || {};
            if (scrollItem) {
                const selectedItem =
                    scrollItem.querySelectorAll &&
                    scrollItem.querySelectorAll(`[data-option="${dimValue}"]`);
                const elOffset = selectedItem && selectedItem[0]?.offsetLeft;
                const elWidth = selectedItem && selectedItem[0]?.offsetWidth;
                const optionWidth = scrollItem?.offsetWidth;
                const myScrollPos = elOffset + elWidth / 2 + optionScrollLeft - optionWidth / 2;
                scrollItem.scrollLeft = myScrollPos;
            }
        }
        return true;
    };

    const customOptionsRenderer = (customizationModel, pricingURL) => {
        return (
            <CustomOptionsConnected
                customizationModel={customizationModel}
                featureFlags={featureFlags}
                ppId={props.productDetails.id}
                dataAutomationId="product-customization-options"
                query={props.query}
                fromMiniPDP={props.fromMiniPDP}
                itemOfferings={pricingURL}
            />
        );
    };

    const renderShowMoreLessProductOptions = (numOfHiddenSwatches) => {
        const colorCarouselWH = ['lg:w-12', 'lg:h-12', 'mdOnly:w-12', 'mdOnly:h-12'];
        const border = ['lg:rounded-3xl', 'mdOnly:rounded-3xl'];
        const showMoreLess = dt([
            ...colorCarouselWH,
            'text-black',
            'font-open-sans',
            'text-normal',
            'font-open-sans-bold',
            'leading-5',
            'border',
            'border-solid',
            'border-gray-25',
            ...border,
        ]);
        return (
            <button type="button" className={showMoreLess} onClick={showMoreLessOnClick}>
                <span>{`${!viewMoreSwatches ? '+' : '-'}${numOfHiddenSwatches}`}</span>
            </button>
        );
    };

    /* Renders a single color swatch */
    const renderProductOptionsColorItem = (
        dimension,
        dataItem,
        index,
        isBeautyDropdown = false
    ) => {
        const { fromMiniPDP } = props;
        const { enableBeautyTemplateSwatch = false, enableColorCarousel = true } = featureFlags;
        const { isDesktop } = deviceType;
        const templateType = pdpProductType && pdpProductType.templateType;
        const shouldRenderMoreOrLessColors = !(
            (templateType === ProductConstants.BEAUTY &&
                enableBeautyTemplateSwatch &&
                !fromMiniPDP) ||
            (shouldRenderMobileView(window?.innerWidth) && enableColorCarousel)
        );
        let colorItem = '';
        // reduce swatchesMaxLn by 1 to accomodate "more/less" link.
        /* istanbul ignore next */
        const displayMoreOrLess = dimension.options.length > swatchesMaxLn + 1;
        const numOfHiddenSwatches = dimension.options.length - swatchesMaxLn;
        const actualSwatchesMaxLen = displayMoreOrLess ? swatchesMaxLn : swatchesMaxLn + 1;
        /* istanbul ignore next */
        const toggleOptionsColorShow =
            !viewMoreSwatches && index > actualSwatchesMaxLen - 1 && shouldRenderMoreOrLessColors
                ? 'hiddenSelect'
                : null;
        const { optionsSelected = [] } = productOptions;

        const optionSelectedColor = optionsSelected?.find(
            (item) => item?.type?.toLowerCase() === 'color'
        );
        const checkSelectedValue = optionSelectedColor?.option === dataItem.value;

        /* istanbul ignore next */
        const colorUnavailable =
            typeof dataItem.availableInStock !== 'undefined' ? !dataItem.availableInStock : null;

        /* istanbul ignore next */
        let colorSelected;
        if (!fromMiniPDP && checkSelectedValue && colorUnavailable) {
            colorSelected = 'colorOOSSelected';
        } else if (!fromMiniPDP && checkSelectedValue && !colorUnavailable) {
            colorSelected = 'colorSelected';
        } else if (fromMiniPDP && checkSelectedValue && !colorUnavailable) {
            colorSelected = 'miniPDPColorSelected';
        } else if (!fromMiniPDP && colorUnavailable) {
            colorSelected = 'colorOOS';
        } else if (fromMiniPDP && !checkSelectedValue && colorUnavailable) {
            colorSelected = 'miniPDPColorOOS';
        } else if (fromMiniPDP && checkSelectedValue && colorUnavailable) {
            colorSelected = 'miniPDPColorOOSSelected';
        }

        let colorUnavailableHTML = null;
        /* istanbul ignore else */
        const selectedValue = checkSelectedValue ? dataItem.value : null;
        // incase if color is not selected by default and has only one color select it by default
        if (selectedValue) {
            selectedSwatchSkuValue = selectedValue;
        }
        if (dataItem.family !== 'More or Less Option') {
            if (colorUnavailable && isBeautyDropdown) {
                colorUnavailableHTML = (
                    <span
                        className={cx('colorUnavailable')}
                        style={{ left: checkSelectedValue ? '20%' : '21%' }}
                    />
                );
            } else if (colorUnavailable) {
                colorUnavailableHTML = <span className={cx('colorUnavailable')} />;
            }

            const missingSwatch = dataItem.image === undefined;
            const lastInRow =
                (index === dimension.options.length - 1 && !displayMoreOrLess) ||
                (viewMoreSwatches && displayMoreOrLess && !((index + 1) % (swatchesMaxLn + 1)));
            const beautyDropdownButton = isBeautyDropdown
                ? dt(['cursor-pointer', 'flex', 'w-full'])
                : '';
            const colorCarouselWidth =
                (!isBeautyDropdown && enableColorCarousel) || fromMiniPDP
                    ? ['smOnly:w-[52px]', 'mdOnly:w-12', 'lg:w-12']
                    : ['w-10'];
            const colorCarouselHeigth =
                (!isBeautyDropdown && enableColorCarousel) || fromMiniPDP
                    ? ['smOnly:h-[52px]', 'mdOnly:h-12', 'lg:h-12']
                    : ['h-10'];
            const optionColorStyle = dt([
                isBeautyDropdown && 'w-8',
                isBeautyDropdown && 'h-8',
                'block',
                'relative',
                'rounded-full',
                'overflow-hidden',
                'm-0',
                'sm:!bg-cover',
                ...colorCarouselWidth,
                ...colorCarouselHeigth,
            ]);
            const colorCarouselMobileView =
                !__SERVER__ && shouldRenderMobileView(window?.innerWidth);
            const miniPDPColorStyle = dt(['mt-0', 'mr-[7px]', 'mb-2', 'ml-0']);
            const colorOOSbgStyle =
                colorUnavailable && !isBeautyDropdown
                    ? dt(['bg-light-white', 'rounded-full', 'aspect-square'])
                    : '';
            const colorOOSBgDropdownStyle = isBeautyDropdown
                ? dt(['bg-light-white', 'rounded-full', 'w-8', 'h-8'])
                : '';
            /* istanbul ignore next */
            const colorMarginStyle =
                !toggleOptionsColorShow && !isBeautyDropdown ? dt(['mr-2', 'mb-2']) : '';
            const miniPDPColorCarouselOOSStyle =
                dataItem?.availabilityMessage.includes(ProductConstants.OUTOFSTOCK) &&
                !checkSelectedValue &&
                fromMiniPDP &&
                !toggleOptionsColorShow
                    ? 'miniPDPColorCarouselOOS'
                    : '';
            colorItem = (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <div
                    className={`${cx(miniPDPColorCarouselOOSStyle, {
                        miniPDPColorCarouselOOSSelected:
                            !toggleOptionsColorShow &&
                            checkSelectedValue &&
                            colorUnavailable &&
                            fromMiniPDP,
                    })} ${dt([colorMarginStyle, beautyDropdownButton, colorOOSbgStyle])}`}
                    onClick={(e) => onChangeHandler(dimension, dataItem, dataItem.value, e)}
                >
                    <div className={colorOOSBgDropdownStyle}>
                        <button
                            type="button"
                            className={`${cx(
                                'optionColor',
                                optionColorStyle,
                                toggleOptionsColorShow,
                                colorSelected,
                                missingSwatch ? 'missingSwatch' : '',
                                lastInRow ? 'lastInRow' : '',
                                isBeautyDropdown && !checkSelectedValue && colorUnavailable
                                    ? 'beautyDropdownOOSStyle'
                                    : ''
                                // { miniPDPColor: fromMiniPDP }
                            )} ${dt([
                                missingSwatch &&
                                    isBeautyDropdown &&
                                    checkSelectedValue &&
                                    'sm:!border-2',
                                missingSwatch && isBeautyDropdown && 'bg-white',
                                missingSwatch && isBeautyDropdown && 'sm:!shadow-none',
                                isBeautyDropdown && 'sm:!mb-0',
                                fromMiniPDP && miniPDPColorStyle,
                            ])}`}
                            onMouseEnter={
                                isDesktop
                                    ? () => debouncedOnColorSwatchEnter(dimension, dataItem)
                                    : undefined
                            }
                            data-automation-id={colorSelected}
                            data-tip
                            data-for={dataItem.value}
                            style={{
                                background: !missingSwatch
                                    ? `url(${dataItem.image.url}?wid=35&hei=35&resmode=sharp2)`
                                    : '',
                            }}
                        >
                            {colorUnavailableHTML}
                            {missingSwatch && !isBeautyDropdown ? (
                                <Icon
                                    iconType="svg"
                                    pathClassName={cx('iconPath imageMissingSwatch')}
                                    width={colorCarouselMobileView ? '44' : '38'}
                                    height={colorCarouselMobileView ? '44' : '38'}
                                    viewBox={
                                        colorCarouselMobileView && checkSelectedValue
                                            ? '-5 -9 48 48'
                                            : '-6 -10 48 48'
                                    }
                                >
                                    <ImgNotAvailablePdp />
                                </Icon>
                            ) : (
                                ''
                            )}
                            {missingSwatch && isBeautyDropdown ? (
                                <Icon
                                    iconType="svg"
                                    pathClassName={cx('iconPath imageMissingSwatch')}
                                    width="20"
                                    height="20"
                                    viewBox="-3 -3 20 20"
                                >
                                    <ImgNotAvailablePdp />
                                </Icon>
                            ) : (
                                ''
                            )}
                        </button>
                    </div>
                    {isBeautyDropdown && (
                        <div className={dt(['my-auto', 'ml-2'])}>
                            <div
                                className={dt([
                                    'text-base',
                                    'leading-5',
                                    colorSelected
                                        ? 'font-open-sans-semibold'
                                        : 'font-open-sans-regular',
                                ])}
                            >
                                {dataItem.value}
                            </div>
                            <div
                                className={dt([
                                    'text-xs',
                                    'text-gray-60',
                                    'font-open-sans-regular',
                                    'leading-3',
                                ])}
                            >
                                {!dataItem.availableInStock && ProductConstants.OUTOFSTOCK}
                            </div>
                        </div>
                    )}
                </div>
            );
        } else {
            colorItem = shouldRenderMoreOrLessColors
                ? renderShowMoreLessProductOptions(numOfHiddenSwatches)
                : null;
        }

        return <>{colorItem}</>;
    };

    const renderErrorMessage = (dimensionId) => {
        const { fromMiniPDP } = props;
        if (messages[dimensionId] && messages[dimensionId].length > 0) {
            return (
                <Messages
                    level={messagesLevels.inline}
                    messages={messages[dimensionId]}
                    fromMiniPDP={fromMiniPDP}
                    hasClose={false}
                />
            );
        }
        return null;
    };

    const checkSizeWarning = (dimensionId) => {
        const { enableErrorSizing } = featureFlags;
        if (enableErrorSizing && messages[dimensionId] && messages[dimensionId].length > 0) {
            return messages[dimensionId][0]?.id === 'SIZEMessageId' || 'WIDTHMessageId';
        }
        return null;
    };

    const renderLotLevelBopisIncentive = (shouldDisplayLotLevelBopisIncentive) => {
        const { productDetails } = props;
        const { enableBopisIncentiveOnTopSec } = featureFlags;
        /* will render lot level bopis incentive message only when
            1) If there are multiple dimensions for colors, this will be displayed only after last color dimension
            2) Current lot is eligible for Bopis Incentive
            3) if the PP is eligible for Bopis based on aggregator call
            4) enableBopisIncentive flag is on
            5) enableBopisIncentiveOnTopSec flag is off (since title will render after product options the requirement is to see the message below color swatches */
        const lotChannelOffering = _get(selectedProductSKU, '[0].channelOffering', '');
        const isBopisEligible =
            productDetails.bopisEligibility && lotChannelOffering !== 'OnlineOnly';
        return (
            !enableBopisIncentiveOnTopSec &&
            shouldDisplayLotLevelBopisIncentive &&
            bopisIncentiveProductEligibility.currentLotEligible &&
            isBopisEligible && (
                <p
                    className={dt([
                        'text-urgency-border',
                        'font-open-sans-semibold',
                        'font-semibold',
                        'text-normal',
                        'my-2',
                    ])}
                >
                    {bopisIncentive.eligibilityMessage}
                </p>
            )
        );
    };

    /* ProductOptions Color UI */
    const renderProductOptionsColor = (dimension, displayLotLevelBopisIncentive) => {
        const { fromMiniPDP } = props;
        const { enableHandleColorizedImage, enableColorCarousel = true } = featureFlags;
        let datasource = [];
        const { isDesktop } = deviceType;

        // eslint-disable-next-line no-param-reassign
        dimension.options =
            dimension.options &&
            dimension.options.length &&
            dimension.options.length > 1 &&
            enableHandleColorizedImage
                ? dimension.options.filter(({ productImage }) => productImage)
                : dimension.options;
        if (dimension.options && dimension.options.length > swatchesMaxLn + 1) {
            datasource = [
                ...dimension.options,
                { family: 'More or Less Option', value: 'more or less Option' },
            ];
        } else {
            datasource = dimension.options;
        }
        const primarySkuValue = _get(dimension, 'options[0].value', '');
        const mobileColorCaraouselStyle = enableColorCarousel
            ? dt([
                  'smOnly:flex',
                  'smOnly:flex-nowrap',
                  'smOnly:overflow-y-scroll',
                  'smOnly:scroll-smooth',
                  'smOnly:-mx-6',
                  'smOnly:w-[calc(100%+48px)]',
                  'smOnly:pl-6',
                  'smOnly:pr-4',
              ])
            : '';
        const mdAboveColorCaraouselStyle =
            enableColorCarousel && !fromMiniPDP
                ? dt(['lg:w-[560px]', 'mdOnly:w-[336px]', 'smOnly:w-full'])
                : dt(['w-full']);

        const miniPdpCarouselStyle =
            fromMiniPDP && enableColorCarousel
                ? dt(['lg:w-[372px]', 'mdOnly:w-[396px]', 'smOnly:w-full'])
                : dt(['w-full']);
        return (
            <div
                className={`${[
                    cx('selectList'),
                    mdAboveColorCaraouselStyle,
                    miniPdpCarouselStyle,
                ].join(' ')} ${dt([props.fromMiniPDP ? 'lg:!w-full' : ''])}`}
                onMouseLeave={
                    isDesktop ? () => debouncedOnColorSwatchLeave(primarySkuValue) : undefined
                }
            >
                <List
                    childRenderer={(dataItem, index) =>
                        renderProductOptionsColorItem(dimension, dataItem, index)
                    } // eslint-disable-line
                    direction="Fluid"
                    automationId={`product-dimensions-${dimension.name}`}
                    datasource={datasource}
                    spacing="None"
                    itemSpacing="None"
                    twoStepRender
                    listStyleClass={`${cx('noScrollBar')} ${mobileColorCaraouselStyle}`}
                    id="productOptionsColorList"
                />
                {/* to display bopis incentive lot level message */}
                {renderLotLevelBopisIncentive(displayLotLevelBopisIncentive)}
            </div>
        );
    };

    const showKALink = (productDetails) => {
        return (
            <KnowledgeAssistance
                productDetails={productDetails}
                placement="top"
                aria-label="knowledgeAssistants"
                automationId="product-option-KnowledgeAssistance"
            />
        );
    };

    /* This is the container UI that has label, options (both dropdown UIs and color UI) and then the grid info. */
    const renderProductOptionsContainer = (dimensionsData) => {
        const { productDetails, fromMiniPDP } = props;

        const { isSwatchesOnHover } = colorSwatches;

        const { optionsSelected = [] } = productOptions;

        const { enableColorSwatchOnTop = true } = featureFlags;

        const dimensions = enableColorSwatchOnTop
            ? replaceColorInDimensions(dimensionsData)
            : [...dimensionsData];

        const colorDimension = reInitializeDimensions?.filter(
            (item) => item?.name?.toLowerCase() === 'color'
        );
        const optionSelectedColor = optionsSelected?.filter(
            (item) => item?.type?.toLowerCase() === 'color'
        );

        let selectedColorDetailsObj = [];
        if (colorDimension?.length) {
            colorDimension.forEach((colorItem) => {
                const tempSelectedColorDetailsObj = colorItem?.options?.filter(
                    (item) =>
                        item?.value?.toLowerCase() === optionSelectedColor?.[0]?.name?.toLowerCase()
                );
                if (tempSelectedColorDetailsObj?.length) {
                    selectedColorDetailsObj = tempSelectedColorDetailsObj;
                }
            });
        }

        let productOptionsList = '';
        let showHidelotFlag = '';
        let kaLink = null;
        const defaultValueRequired = true;
        const colorRow = filter(dimensions, { id: 'COLOR' }); // length's greater than 1 if product's hybrid
        if (!enableColorSwatchOnTop) {
            dimensions.splice(dimensions.length - colorRow.length, 0, {
                bvTrueToFit: true,
            });
        }
        let selectedColorName = '';
        let displayColorName = true;
        const loaderDisplayed = {};
        const isPriceDisplayAvailable = some(dimensions, { priceDisplay: false });
        // to perform calculations inorder to display lot level bopis incentive message at the end of last color dimension.
        let numOfColorDimensions = countBy(dimensions, (dimension) => dimension.id === 'COLOR')
            .true;
        let colorCounter = 0;
        const selectedColor = find(optionsSelected, { type: 'COLOR' });
        const extendedSizeDetails =
            !fromMiniPDP && featureFlags.enableExtendedSizing
                ? _get(productDetails, 'ppToPPAssociations', [])
                : [];
        /* istanbul ignore next */
        const congruentPPsDetails =
            !fromMiniPDP && featureFlags.enableProductSelection
                ? _get(productDetails, 'congruentDimensions', [])
                : [];
        const renderDimensions = dimensions.map((dimension) => {
            const productOptionsContainer = '';
            const isDimensionColor = dimension.id === 'COLOR';
            if (dimension.bvTrueToFit) {
                return (
                    <div className={dt(['relative', 'top-1'])} id="BVRRSecondarySummaryContainer" />
                );
            }
            /* istanbul ignore next */
            showHidelotFlag =
                dimension.type === 'lot' && ProductModule.getShowHidelotFlag(productType)
                    ? 'hideOptions'
                    : 'showOptions';
            const dimensionWithUnavailableFiltersRemoved = ProductModule.removeUnavailableFilters(
                dimension
            );
            const shouldDisplayLoader = !featureFlags.enableFusionApp && !inventory[0];
            // will be set to true when we reach last color dimension
            let displayLotLevelBopisIncentive = false;
            // display options when data is ready and loader is manually terminated
            if (!shouldDisplayLoader) {
                if (isDimensionColor) {
                    // adds 1 for each color dimension
                    colorCounter += 1;
                    if (isEmpty(dimensionWithUnavailableFiltersRemoved.options)) {
                        // subtract 1 when a color dimension is not available
                        numOfColorDimensions -= 1;
                        colorCounter -= 1;
                    }
                    selectedColorName = _get(selectedColor, 'name', '');
                    // to decide wether last color dimension that is displayed is reached
                    displayLotLevelBopisIncentive = colorCounter === numOfColorDimensions;
                    productOptionsList = displayLotLevelBopisIncentive
                        ? renderProductOptionsColor(
                              dimensionWithUnavailableFiltersRemoved,
                              displayLotLevelBopisIncentive
                          )
                        : renderProductOptionsColor(dimensionWithUnavailableFiltersRemoved);
                } else {
                    // if the filters are selected then availabilityMessage is set for each option object. If filters are not
                    // selected then availabilityMessage is undefined.
                    let value = getDefaultValueSelected(dimensionWithUnavailableFiltersRemoved.id);
                    if (
                        dimension.name === 'size' &&
                        dimension.options.some(
                            (x) => x.value.match(/[a-z]/gi) && x.value === x.value.toUpperCase()
                        )
                    ) {
                        value = value.toUpperCase();
                    }
                    const dimensionName = dimensionWithUnavailableFiltersRemoved.name;
                    let isCongruentOption = false;
                    congruentPPsDetails?.map((element) => {
                        const iscongruentPPEligible =
                            dimension.name === element.name &&
                            element.options?.length > productPillsLimit;
                        if (iscongruentPPEligible) {
                            isCongruentOption = true;
                        }
                    });
                    isCongruentOption = congruentPPsDetails.length > 0 && dimension.name === 'size';
                    const selectDropdownStyle = dt([
                        'lg:w-3/5',
                        'border',
                        'border-soild',
                        checkSizeWarning(dimension.id) && '!border-[#c00]',
                    ]);

                    const renderProductOption =
                        _get(dimensionWithUnavailableFiltersRemoved, 'options.length') <=
                            productPillsLimit ||
                        (featureFlags.enableProductSelection && isCongruentOption) ? (
                            <ProductOptionPills
                                productOptions={dimensionWithUnavailableFiltersRemoved}
                                onOptionClick={onChangeHandler}
                                selectedOption={value}
                                checkSizeWarning={checkSizeWarning(dimension.id)}
                                extendedSizeDetails={extendedSizeDetails}
                                congruentPPsDetails={congruentPPsDetails}
                                isMobile={deviceType.isMobile}
                            />
                        ) : (
                            <SelectDropdown
                                name={dimensionName}
                                automationId={`product-dimensions-${dimensionName}`}
                                datasource={dimensionWithUnavailableFiltersRemoved.options}
                                defaultValueRequired={defaultValueRequired}
                                optionDisplayValue="availabilityMessage"
                                optionValue="value"
                                selectStyle={showHidelotFlag}
                                theme={`${selectDropdownStyle} selectdropdowndiv`}
                                onChangeHandler={(changedValue) =>
                                    onChangeHandler(
                                        dimensionWithUnavailableFiltersRemoved,
                                        null,
                                        changedValue
                                    )
                                }
                                value={value}
                                regularItem
                            />
                        );
                    productOptionsList = <div>{renderProductOption}</div>;
                }
            } else {
                /* istanbul ignore next */
                if (!loaderDisplayed[dimension.id]) {
                    productOptionsList = (
                        <ProductOptionsPlaceHolder
                            dimension={dimension}
                            fromMiniPDP={fromMiniPDP}
                        />
                    );
                    loaderDisplayed[dimension.id] = false;
                } else {
                    productOptionsList = '';
                }
            }
            if (isSizeDimension(dimension)) {
                kaLink = (
                    <div
                        className={dt(['w-3/6', 'p-0', 'float-right'])}
                        aria-label="KnowledgeAssistance"
                    >
                        {displayTopSizeChart && showKALink(productDetails)}
                    </div>
                );
            } else {
                kaLink = null;
            }
            let dimensionName = '';
            const COLOR = 'color';
            const SIZE = 'size';
            const dimensionWithUnavailableFiltersLength =
                dimensionWithUnavailableFiltersRemoved.options.length;
            const dimensionId =
                dimension.priceType === 'ORIGINAL' ||
                dimension.priceType === 'SALE' ||
                dimension.priceType === 'CLEARANCE'
                    ? dimension.priceType
                    : dimension.id;
            if (
                dimension.isSaleandClearence &&
                displayColorName &&
                dimensionWithUnavailableFiltersLength
            ) {
                dimensionName =
                    featureFlags.enableProductSelection && dimension.name !== COLOR
                        ? dimension.displayName
                            ? dimension.displayName
                            : dimension.name
                        : dimension.name;
                displayColorName = false;
                if (!isSwatchesOnHover) {
                    renderSecondaryDimensionValue = (
                        <SecondaryDimension
                            dimension={
                                ProductModule.removeUnavailableFilters(
                                    find(productDetails.dimensions, { id: 'COLOR' })
                                ) || {}
                            }
                            option={selectedColorName}
                            fromMiniPDP={fromMiniPDP}
                        />
                    );
                }
            } else if (!dimension.isSaleandClearence) {
                dimensionName =
                    featureFlags.enableProductSelection && dimension.name !== COLOR
                        ? dimension.displayName
                            ? dimension.displayName
                            : dimension.name
                        : dimension.name;
                if (!isSwatchesOnHover) {
                    renderSecondaryDimensionValue = (
                        <SecondaryDimension
                            dimension={dimensionWithUnavailableFiltersRemoved}
                            option={selectedColorName}
                            fromMiniPDP={fromMiniPDP}
                        />
                    );
                }
            }
            const templateType = pdpProductType && pdpProductType.templateType;
            const isBeautyDropDown = templateType === ProductConstants.BEAUTY;
            const wrapperId = dimension.priceType
                ? `option-wrapper-${dimension.priceType}`
                : `option-wrapper-${fromMiniPDP}`;
            const { productImage, altImages, images } = selectedColorDetailsObj?.[0] || {};
            const { modelHeight, modelWearingSize } =
                getImageObject(productImage, images, altImages) || {};

            /* istanbul ignore next */
            const returnValue =
                dimensionWithUnavailableFiltersLength > 0 ? (
                    <div
                        className={`${dt([
                            'relative',
                            'p-0',
                            'print:w-1/2',
                            'print:inline-block',
                            'print:align-top',
                            'w-full',
                        ])} ${cx(productOptionsContainer, {
                            colorWrapper: isDimensionColor,
                            selectDropdown: featureFlags.enableProductSelection
                                ? !isDimensionColor
                                : dimension.isNeedToBePadded,
                            noMargin: shouldDisplayLoader && isDimensionColor && props.fromMiniPDP,
                        })}`}
                        id={wrapperId}
                    >
                        <div
                            className={cx('wrapper', { miniPDPWrapper: fromMiniPDP })}
                            id={`product-options-${fromMiniPDP}`}
                            data-automationid="production-option-block"
                        >
                            {!featureFlags.enableErrorSizing && renderErrorMessage(dimensionId)}
                            {
                                <div
                                    className={`${cx(
                                        'dimensionName',
                                        'row',
                                        showHidelotFlag
                                    )} ${dt(['pb-2'])}`}
                                >
                                    {/* Take col12 space if kaLink is not there */}
                                    <div
                                        className={cx(
                                            'dimensionNameBlock',
                                            isSizeDimension(dimension) ? 'col6' : 'col12'
                                        )}
                                    >
                                        <div
                                            id={`display-color-text-${fromMiniPDP}`}
                                            aria-label={dimension.name}
                                            data-automation-id={dimension.name}
                                            className={dt([
                                                'font-open-sans-semibold',
                                                checkSizeWarning(dimension.id)
                                                    ? 'text-[#DC2626]'
                                                    : '',
                                            ])}
                                        >
                                            {dimensionName &&
                                                dimensionName !== COLOR &&
                                                dimensionName}
                                            {featureFlags.enableBeautyDropdown &&
                                                !fromMiniPDP &&
                                                isBeautyDropDown &&
                                                isDimensionColor && (
                                                    <BeautyDropDown
                                                        fromMiniPDP={fromMiniPDP}
                                                        dimension={dimension}
                                                        // eslint-disable-next-line react/jsx-no-bind
                                                        renderProductOptionsColorItem={
                                                            renderProductOptionsColorItem
                                                        }
                                                        optionsSelected={optionsSelected}
                                                    />
                                                )}
                                            {dimensionName === COLOR &&
                                                dimension.options &&
                                                renderSecondaryDimensionValue}
                                        </div>
                                        <div
                                            className={cx({
                                                dimensionHybridFirstBlock:
                                                    dimension.isSaleandClearence &&
                                                    !isPriceDisplayAvailable &&
                                                    dimensionName === COLOR &&
                                                    dimension.options,
                                            })}
                                        >
                                            {dimension.isSaleandClearence &&
                                                !isPriceDisplayAvailable && (
                                                    <span className={dt(['text-base'])}>
                                                        {renderPriceForDimensions(
                                                            dimension,
                                                            shouldDimensionPriceDisplay(inventory),
                                                            saleAndClearence
                                                        )}
                                                    </span>
                                                )}
                                            {dimension.isSaleandClearence &&
                                            dimensionWithUnavailableFiltersLength > 0 &&
                                            !isPriceDisplayAvailable ? (
                                                <span
                                                    className={cx('priceType')}
                                                    aria-label={dimension.name}
                                                    data-automation-id={dimension.name}
                                                >
                                                    {dimension.priceType &&
                                                    dimension.priceType.toLowerCase() !== 'original'
                                                        ? dimension.priceType
                                                        : ''}
                                                </span>
                                            ) : (
                                                ''
                                            )}
                                        </div>
                                    </div>
                                    {kaLink}
                                </div>
                            }
                            {productOptionsList && (
                                <div className={cx('product-options-list-wrapper')}>
                                    {productOptionsList}
                                </div>
                            )}
                        </div>
                        {dimensionName === SIZE ? (
                            <div className={dt(['relative'])}>
                                {!featureFlags?.enableTrueToSizeIndicator &&
                                    featureFlags?.enableModelSizing &&
                                    modelHeight?.toString() &&
                                    modelWearingSize?.toString() && (
                                        <div
                                            className={dt([
                                                'mt-6',
                                                'mb-4',
                                                'smMd:my-6',
                                                'text-sm',
                                                'font-sans',
                                            ])}
                                        >
                                            {`Model is ${modelHeight || ''} and wearing US ${
                                                modelWearingSize || ''
                                            }`}
                                        </div>
                                    )}
                            </div>
                        ) : null}
                    </div>
                ) : (
                    ''
                );
            // render a dimension or render lot level bopis incentive message.
            return returnValue || productOptionsList;
        });
        return renderDimensions;
    };

    const renderDimensions = () => {
        const { productDetails, fromMiniPDP } = props;
        const {
            disableDataSizeChart = false,
            enableTrueToSizeIndicator = false,
            enableModelSizing = false,
        } = featureFlags;
        const { selectedProductLot = [], optionsSelected = [], pricingURL = {} } = productOptions;
        const customizationModel =
            selectedProductLot && selectedProductLot[0] && selectedProductLot[0].customizationModel
                ? selectedProductLot[0].customizationModel
                : {};
        const isDimensionPresent = reInitializeDimensions && reInitializeDimensions.length;
        const pdpSelectedSKU = pricingURL.itemOfferingCall ? skuId : '';
        const colorDimension = reInitializeDimensions?.filter(
            (item) => item?.name?.toLowerCase() === 'color'
        );
        const optionSelectedColor = optionsSelected?.filter(
            (item) => item?.type?.toLowerCase() === 'color'
        );

        let selectedColorDetailsObj = [];
        if (colorDimension?.length) {
            colorDimension.forEach((colorItem) => {
                const tempSelectedColorDetailsObj = colorItem?.options?.filter(
                    (item) =>
                        item?.value?.toLowerCase() === optionSelectedColor?.[0]?.name?.toLowerCase()
                );
                if (tempSelectedColorDetailsObj?.length) {
                    selectedColorDetailsObj = tempSelectedColorDetailsObj;
                }
            });
        }
        const { productImage, altImages, images } = selectedColorDetailsObj?.[0] || {};
        const { modelHeight, modelWearingSize } =
            getImageObject(productImage, images, altImages) || {};
        return (
            <>
                <section id={`dimensions-${fromMiniPDP}`}>
                    {isDimensionPresent
                        ? renderProductOptionsContainer(reInitializeDimensions)
                        : renderLotLevelBopisIncentive(true)}
                    {customizationModel.type
                        ? customOptionsRenderer(customizationModel, pricingURL)
                        : null}
                </section>
                {enableTrueToSizeIndicator && !fromMiniPDP && (
                    <TrueToSizeIndicator
                        ppId={productDetails.id}
                        modelHeight={modelHeight}
                        modelWearingSize={modelWearingSize}
                        enableModelSizing={enableModelSizing}
                    />
                )}
                {!disableDataSizeChart && displayTopSizeChart && isDimensionPresent ? (
                    <DataSizeChart
                        optionsSelected={optionsSelected}
                        disableDataSizeChart={disableDataSizeChart}
                        showKALink={showKALink}
                        productDetails={productDetails}
                        selectedSKUId={pdpSelectedSKU}
                    />
                ) : null}
            </>
        );
    };

    const handleScrollOnLoad = () => {
        const {
            productDetails: { hybridIndicator },
        } = props;
        const selectedColor = find(currentFilters, { type: 'COLOR' }) || {};
        const selectedColorName = selectedColor.name || '';
        let priceType;
        if (hybridIndicator) {
            const colorOptions = reInitializeDimensions.filter((options) => options.id === 'COLOR');
            colorOptions.forEach((colorDimension) => {
                find(colorDimension.options, (option) => {
                    if (option.value === selectedColorName) {
                        priceType = colorDimension.priceType;
                    }
                });
            });
        }
        handleMobileColorSelectionBehaviour(
            selectedColorName,
            null,
            priceType,
            24,
            false,
            true
        ); /* scrollItem has left padding of 24px, hence , added this to make the selected swatch look centered */
    };

    useEffect(() => {
        const congruentOptions =
            !fromMiniPDP && featureFlags.enableProductSelection
                ? _get(props.productDetails, 'congruentOptions', [])
                : [];
        triggerSDPUStoreInfo();

        /* istanbul ignore else */
        if (congruentOptions?.length === 0 && !props.fromMiniPDP) {
            getInventory();
        }
        if (!fromMiniPDP) {
            window.onpopstate = function () {
                if (!hasExecuted) {
                    hasExecuted = true;
                }
            };
        }
        handleScrollOnLoad();
    }, []);

    useEffect(() => {
        if (congruentSelected.length === 0 && !props.fromMiniPDP && hasExecuted) {
            hasExecuted = false;
        }
    }, [props.productDetails?.id]);

    useEffect(() => {
        const congruentOptions =
            !fromMiniPDP && featureFlags.enableProductSelection
                ? _get(props.productDetails, 'congruentOptions', [])
                : [];
        if (!hasExecuted) {
            if (
                congruentOptions?.length > 0 &&
                ((Object.keys(congruentSelected)?.length > 0 && !congruentSelected?.isCongruent) ||
                    Object.keys(congruentSelected)?.length === 0) &&
                productOptions.congruentFilters.length === 0 &&
                !props.fromMiniPDP
            ) {
                getInventory();
                dispatch(MessagesActions.resetMessages({ sourceMiniPDP: props.fromMiniPDP }));
            }
        }
    }, [props.productDetails?.id, hasExecuted, congruentSelected]);

    useEffect(() => {
        if (
            !(isEmpty(selectedStore.id) && isEmpty(storeID) && isEmpty(sdpuStoreInfo.id)) &&
            (selectedStore.id !== storeID || sdpuStoreInfo.id !== storeID) &&
            !props.fromMiniPDP
        ) {
            dispatch(productOptionsOnResetAction(props.fromMiniPDP));
            getInventory();
        }
        if (
            Object.keys(congruentSelected)?.length > 0 &&
            productOptions.congruentFilters.length === 0 &&
            congruentSelected?.isCongruent &&
            !props.fromMiniPDP
        ) {
            dispatch(
                productOptionsOnClickAction({
                    optionsSelected: productOptions.optionsSelected,
                    isCongruent: false,
                })
            );
        }
    }, [props.productDetails?.id, userPreferredStore.storeId]);

    useEffect(() => {
        /* istanbul ignore else */
        if (
            !isOOSAnalyticComplete &&
            productOptions.isProductInStock !== '' &&
            !productOptions.isProductInStock &&
            props.analytics &&
            props.analytics.pageReadyCmpleted
        ) {
            /* istanbul ignore next */
            setTimeout(() => {
                dispatch(productOOSAnalytics());
            }, 3000);
            setIsOOSAnalyticComplete(true);
        }
    }, [
        isOOSAnalyticComplete,
        productOptions?.isProductInStock,
        props.analytics?.pageReadyCmpleted,
    ]);

    useEffect(() => {
        if (inventory.length && !viewMoreSwatches) {
            const { productDetails, fromMiniPDP } = props;
            const selectedColor = find(productOptions.optionsSelected, { type: 'COLOR' });
            const selectedColorName = _get(selectedColor, 'name', '');
            const colorOptions = productOptions.dimensions.filter(
                (optionSelected) => optionSelected.id === 'COLOR'
            );
            const congruentOptions =
                !fromMiniPDP && featureFlags.enableProductSelection
                    ? _get(productDetails, 'congruentOptions', [])
                    : [];
            let dimensionWithUnavailableFiltersRemoved;
            let dimensionIndex = 0;

            if (colorOptions.length > 1) {
                colorOptions.forEach((option) => {
                    dimensionWithUnavailableFiltersRemoved = ProductModule.removeUnavailableFilters(
                        option
                    );
                    if (
                        dimensionWithUnavailableFiltersRemoved &&
                        dimensionWithUnavailableFiltersRemoved.options
                    ) {
                        dimensionIndex = findIndex(
                            dimensionWithUnavailableFiltersRemoved.options,
                            (optionValue) =>
                                optionValue.value.toLowerCase() === selectedColorName.toLowerCase()
                        );
                    }
                    shouldViewMoreSwatches(dimensionIndex);
                });
            } else {
                dimensionWithUnavailableFiltersRemoved = ProductModule.removeUnavailableFilters(
                    colorOptions
                );
                if (
                    dimensionWithUnavailableFiltersRemoved &&
                    dimensionWithUnavailableFiltersRemoved[0]
                ) {
                    dimensionIndex = findIndex(
                        dimensionWithUnavailableFiltersRemoved[0].options,
                        (option) => option.value.toLowerCase() === selectedColorName.toLowerCase()
                    );
                }
                shouldViewMoreSwatches(dimensionIndex);
            }
            if (congruentOptions.length > 0 && deviceType.isMobile) {
                productOptions.optionsSelected?.forEach((option) => {
                    option.type !== 'COLOR' &&
                        handleMobileOptionsSelectionBehaviour(option, option.name, 24);
                });
            }
        }
    }, [inventory?.length]);

    useEffect(() => {
        const templateType = pdpProductType && pdpProductType.templateType;
        if (
            featureFlags.enalbleSkuSelectionStorage &&
            templateType !== 'bundle' &&
            templateType !== 'mtm' &&
            templateType !== 'vdata'
        ) {
            const lotId = _get(productOptions, 'selectedLotDetails.id', 'NA');
            storeSkuInfo(props.productDetails?.id, lotId, skuId, isSelectedItemAvailable);
        }
    }, [currentFilters, skuId, isSelectedItemAvailable]);

    useEffect(() => {
        const { fromMiniPDP, productDetails } = props;
        const { triggerSelectSkuEventForMiniPDP = false } = featureFlags;

        if (
            fromMiniPDP &&
            triggerSelectSkuEventForMiniPDP &&
            pricingDetails?.offeringCall &&
            productOfferingData.actualSkuId === skuId
        ) {
            const miniPDPProductAvailibilityData = _get(
                productOfferingData,
                'promise.skuDeliveryOptions[0].deliveryOptions.shipToHome',
                {}
            );
            dispatch(
                allSKUSelected(
                    getDeliveryDateAnalytics(
                        {},
                        productDetails,
                        skuId,
                        selectedProductSKU,
                        pricingDetails,
                        [],
                        fromMiniPDP,
                        { shipToHomeAvailability: miniPDPProductAvailibilityData }
                    )
                )
            );
        }
    }, [skuId, pricingDetails?.offeringCall, productOfferingData.actualSkuId]);

    /* istanbul ignore next */
    useEffect(() => {
        const { productDetails, fromMiniPDP } = props;
        const congruentOptions =
            !fromMiniPDP && featureFlags.enableProductSelection
                ? _get(productDetails, 'congruentOptions', [])
                : [];
        const convertDataToKeyValueObject = (data) => {
            const result = {};
            data.forEach((item) => {
                result[item?.type?.split(' ').join('_').toUpperCase()] = item?.option;
            });
            return result;
        };

        const requiredKeys = convertDataToKeyValueObject(productOptions.optionsSelected);

        const matchesRequiredKeys = (options, requiredKeys) => {
            return Object.keys(options).every(
                (key) => options[key] && options[key].includes(requiredKeys[key])
            );
        };

        const matchingUrls = congruentOptions
            .filter((item) =>
                item?.options.some((option) => matchesRequiredKeys(option, requiredKeys))
            )
            .map((item) => item?.url);
        if (
            productOptions.congruentFilters?.length > 0 &&
            matchingUrls?.length > 0 &&
            !congruentSelected?.isCongruent
        ) {
            productOptions.optionsSelected?.length > 0 &&
                dispatch(
                    productOptionsOnClickAction({
                        optionsSelected: productOptions.optionsSelected,
                        isCongruent: true,
                    })
                );
            getBrowserHistory().push(matchingUrls[0]);
        }
    }, [productOptions.optionsSelected, productOptions.congruentFilters, congruentSelected]);

    const { fromMiniPDP } = props;
    const { enableErrorSizing = false } = featureFlags;
    return (
        <section
            id={`globaleoptions-${fromMiniPDP}`}
            className={`${(enableErrorSizing && dt(['mb-4'])) || ''} ${cx(
                { fromMiniPDP },
                'GlobalOptions-Container'
            )}`}
        >
            {renderDimensions()}
        </section>
    );
};

ProductOptions.defaultProps = {
    fromMiniPDP: false,
    productDetails: {},
    query: {},
};

ProductOptions.propTypes = {
    analytics: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
    fromMiniPDP: PropTypes.bool,
    productDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    query: PropTypes.object,
};

export default ProductOptions;
